import {
  InfoCard,
  MarkdownContent,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import useAsync from 'react-use/esm/useAsync';

const SLUG = 'github.com/project-slug';

const ReadmeCard = () => {
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const slug = entity.metadata.annotations?.[SLUG] ?? '';
  const [, repo] = slug.split('/');

  const { value, loading, error } = useAsync(async () => {
    const base = await discovery.getBaseUrl('readme');
    const res = await fetch(`${base}/${encodeURIComponent(repo)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`README request failed: ${res.status}`);
    return res.text();
  }, [repo]);

  // Relative links in a README point into the repo, not into Backstage.
  const blob = `https://github.com/${slug}/blob/HEAD/`;
  const toRepo = (href: string) =>
    /^([a-z]+:|#|\/\/)/i.test(href) ? href : blob + href.replace(/^\.?\//, '');

  return (
    <InfoCard title="README">
      {loading && <Progress />}
      {error && (
        <WarningPanel
          title="Could not load the README"
          message={error.message}
        />
      )}
      {value === null && 'This repository has no README.'}
      {value && (
        <MarkdownContent
          content={value}
          dialect="gfm"
          transformLinkUri={toRepo}
          transformImageUri={href => toRepo(href).replace('/blob/', '/raw/')}
        />
      )}
    </InfoCard>
  );
};

export const readmeModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    EntityCardBlueprint.make({
      name: 'readme',
      params: {
        type: 'content',
        filter: entity => Boolean(entity.metadata.annotations?.[SLUG]),
        loader: async () => <ReadmeCard />,
      },
    }),
  ],
});
